import type { SaveDraftItem } from '@shared/components/save-draft-modal/save-draft-modal.model';
import type { RetrieveDraftItem } from '@shared/services/draft/draft.service';
import type { MinorDraft } from '@shared/services/hydration/ku4-draft.models';
import { sourceAccountIds } from '@shared/services/hydration/mappers/account-details.mapper';

/**
 * Is the draft about to be offered still usable?
 *
 * Resume matches the saved selection by account id, so if an account is no longer transferable
 * the client lands on a form that looks blank and cannot be completed.
 */
export function findStaleMinorDraft(
  drafts: SaveDraftItem[],
  rawDrafts: RetrieveDraftItem[],
  eligibleAccountIds: string[],
): SaveDraftItem | null {
  const offered = drafts.length ? drafts[0] : null;

  if (!offered) {
    return null;
  }

  const raw = rawDrafts.find((item) => item.draftId === offered.id);
  const savedIds = sourceAccountIds((raw?.draftDetails as MinorDraft | undefined)?.accountDetails);

  if (!savedIds.length) {
    return null;
  }

  const eligibleIds = new Set(eligibleAccountIds);

  return savedIds.some((accountId) => !eligibleIds.has(accountId)) ? offered : null;
}

/**
 * Sweeps all the client's minor drafts when no account is eligible. A draft accumulates per
 * abandoned session, and declining the resume prompt only ever deletes the newest.
 *
 * Exit path only. On flow entry an empty draft is still usable, since the client picks from
 * whatever is currently eligible.
 */
export function minorDraftIdsToDiscardOnExit(
  drafts: SaveDraftItem[],
  rawDrafts: RetrieveDraftItem[],
  eligibleAccountIds: string[],
): string[] {
  if (!eligibleAccountIds.length) {
    return drafts.flatMap((draft) => draft.allIds);
  }

  const eligibleIds = new Set(eligibleAccountIds);

  return drafts
    .filter((draft) => {
      const raw = rawDrafts.find((item) => item.draftId === draft.id);
      const savedIds = sourceAccountIds(
        (raw?.draftDetails as MinorDraft | undefined)?.accountDetails,
      );

      return savedIds.some((accountId) => !eligibleIds.has(accountId));
    })
    .flatMap((draft) => draft.allIds);
}
